'use client';

import { ChevronDownIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import type { Data } from '@/types/chart';
import { cn } from '@/utils/ui-utils';
import DataPointList from './modules/DataPointList';

const COLLAPSED_HEIGHT = 66;

interface VisualHeaderProps {
  title: string;
  description?: string;
  latestDataPoints: Data[] | null;
  showLatestDataPoints: boolean;
  unit?: string;
  className?: string;
}

const contentBottom = (el: HTMLElement | null, container: HTMLElement) =>
  el ? el.getBoundingClientRect().bottom - container.getBoundingClientRect().top : 0;

export function VisualHeader({
  title,
  description,
  showLatestDataPoints,
  latestDataPoints,
  unit,
  className,
}: VisualHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const dataPointsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const content = contentRef.current;
    const text = textRef.current;
    const dataPoints = dataPointsRef.current;
    if (!content || !text || !dataPoints) {
      return;
    }

    const measure = () => {
      const bottom = Math.max(contentBottom(descriptionRef.current, content), contentBottom(dataPoints, content));
      setIsExpandable(bottom > COLLAPSED_HEIGHT);
    };
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(text);
    resizeObserver.observe(dataPoints);
    return () => resizeObserver.disconnect();
  }, []);

  const canToggle = isExpandable || isOpen;
  const toggle = () => setIsOpen((open) => !open);
  const currentVariant = isOpen ? 'open' : 'closed';

  return (
    <div
      className={cn(
        'group mobile-xs:flex-col mobile-xs:gap-2 relative flex items-start justify-between gap-4 px-5 pt-5 pb-5',
        className,
      )}
    >
      <motion.div
        ref={contentRef}
        className='flex w-full items-start justify-between overflow-clip print:h-auto'
        initial={currentVariant}
        animate={currentVariant}
        variants={{
          open: {
            height: 'auto',
          },
          closed: {
            height: COLLAPSED_HEIGHT,
          },
        }}
      >
        <div ref={textRef}>
          <h3 className='text-primary text-base font-semibold text-balance'>
            {canToggle ? (
              <button
                type='button'
                aria-expanded={isOpen}
                onClick={toggle}
                className='expanded-link focus-visible:before:ring-ring cursor-pointer text-start focus-visible:outline-hidden
                  focus-visible:before:rounded-md focus-visible:before:ring-2 focus-visible:before:ring-inset'
              >
                {title}
                <ChevronDownIcon
                  size={16}
                  strokeWidth={1.5}
                  className={cn(
                    'text-muted-foreground ml-1 inline-block align-[-2px] transition-transform duration-300 print:hidden',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
            ) : (
              title
            )}
          </h3>
          <span
            ref={descriptionRef}
            className='text-muted-foreground mt-0.5 block text-wrap'
          >
            {description}
          </span>
        </div>
        <div
          ref={dataPointsRef}
          className='mt-0.5 mb-5 flex flex-col gap-2'
        >
          {showLatestDataPoints && (
            <DataPointList
              latestDataPoints={latestDataPoints}
              unit={unit}
            />
          )}
        </div>
      </motion.div>
      {isExpandable && !isOpen && (
        <div
          aria-hidden
          className='from-background mobile:hidden pointer-events-none absolute inset-x-0 bottom-5 z-10 h-3 bg-linear-to-t
            to-transparent transition-opacity duration-300 group-active:opacity-0'
        />
      )}
    </div>
  );
}
